import type { ColumnDef, OnChangeFn, RowSelectionState, SortingState } from '@tanstack/react-table';
import { getCoreRowModel, useReactTable } from '@tanstack/react-table';
import type {
    BillingStatus,
    CustomDateRange,
    ExplorerViewFilters,
    VisibilityWindow,
} from '../../../server/utils/explorer-utils.ts';
import { DataTable } from '../../list-views/components/data-table.tsx';
import type { ExplorerTableData } from '../hooks/use-configurable-explorer-columns.tsx';
import { useExplorerTotalSummary } from '../hooks/use-explorer-total-summary.tsx';

type ExplorerTableProps = {
    data: ExplorerTableData[];
    columns: ColumnDef<ExplorerTableData>[];
    fetchNextPage: () => void;
    isFetchingNextPage: boolean;
    isFetching: boolean;
    hasNextPage?: boolean;
    isLoading: boolean;
    filters: ExplorerViewFilters;
    window: VisibilityWindow;
    billingStatus: BillingStatus;
    customDateRange: CustomDateRange | undefined;
    rowSelection: RowSelectionState;
    setRowSelection: OnChangeFn<RowSelectionState>;
    sorting: SortingState;
    sortChangedInInterface: OnChangeFn<SortingState>;
};

export const ExplorerDataTable = ({
    data,
    columns,
    fetchNextPage,
    isFetchingNextPage,
    hasNextPage,
    isLoading,
    rowSelection,
    setRowSelection,
    sorting,
    sortChangedInInterface,
}: ExplorerTableProps) => {
    const { summaryHeader } = useExplorerTotalSummary();

    const table = useReactTable({
        data,
        columns,
        getCoreRowModel: getCoreRowModel(),
        onRowSelectionChange: setRowSelection,
        enableMultiRowSelection: true,
        onSortingChange: sortChangedInInterface,
        state: {
            rowSelection,
            sorting,
        },
    });

    return (
        <DataTable
            isLoading={isLoading}
            isFetchingNextPage={isFetchingNextPage}
            onFetchNextPage={fetchNextPage}
            hasNextPage={hasNextPage}
            table={table}
            columns={columns}
            summaryHeader={summaryHeader}
        />
    );
};
